import React, { useEffect, useCallback, useState } from 'react';
import { useHandleError } from '../../services';
import _ from 'lodash';
import { gqlGetCollectionOrders, gqlGetCollPointContainers, gqlGetImaPdf } from './graphql';
import { toastWarning, toastSuccess } from 'utils/toasts';
import { useLazyQuery, useMutation, useApolloClient, useQuery } from '@apollo/client';
import { Card, Badge, Button } from 'react-bootstrap';
import { FaFilePdf, FaPlus } from 'react-icons/fa';
import { FlexRow } from 'motion-components';
import NewCollectionOrderModal from './co-modal';

interface ContainersOrdersSectionProps {
  id: number;
}

/** Εμφανής πρόσκληση για νέα αίτηση */
const NewCollectionOrderCta = ({ onClick }: { onClick: () => void | Promise<void> }) => (
  <div className="home-callout home-callout--primary">
    <p>Θέλετε να ζητήσετε συλλογή αποβλήτων; Υποβάλετε νέα αίτηση.</p>
    <Button
      variant="primary"
      className="home-cta-btn home-cta-btn--wide"
      onClick={onClick}
    >
      <FaPlus className="mb-1 mr-1" /> Νέα Αίτηση Συλλογής
    </Button>
  </div>
);

/** Λήψη αναφοράς ΗΜΑ — ίδιο frame με την αίτηση συλλογής */
const ImaPdfCta = ({ onClick }: { onClick: () => void }) => (
  <div className="home-callout home-callout--success">
    <p>
      <span className="home-callout__label">
        Λήψη στοιχείων για την ετήσια δήλωση στο Ηλεκτρονικό Μητρώο Αποβλήτων
      </span>
    </p>
    <Button variant="success" className="home-cta-btn home-cta-btn--medium" onClick={onClick}>
      <FaFilePdf className="mb-1 mr-1" /> ΗΜΑ
    </Button>
  </div>
);

/** Γραμμή ενεργειών: CTA + λήψη ΗΜΑ */
const OrdersActionsToolbar = ({
  showCta,
  onNewOrder,
  onPdfClick,
}: {
  showCta: boolean;
  onNewOrder: () => void | Promise<void>;
  onPdfClick: () => void;
}) => (
  <div className="home-actions-toolbar">
    {showCta && <NewCollectionOrderCta onClick={onNewOrder} />}
    <ImaPdfCta onClick={onPdfClick} />
  </div>
);

export const CollectionOrdersSection: React.FC<ContainersOrdersSectionProps> = ({ id }) => {
  const { handleGraphqlError } = useHandleError('newcollectionorder');
  const SortIcons = {
    asc: (
      <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height="1em"
        width="1em"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: '0.3rem', marginTop: '-4px' }}
      >
        <path d="M34.9 289.5l-22.2-22.2c-9.4-9.4-9.4-24.6 0-33.9L207 39c9.4-9.4 24.6-9.4 33.9 0l194.3 194.3c9.4 9.4 9.4 24.6 0 33.9L413 289.4c-9.5 9.5-25 9.3-34.3-.4L264 168.6V456c0 13.3-10.7 24-24 24h-32c-13.3 0-24-10.7-24-24V168.6L69.2 289.1c-9.3 9.8-24.8 10-34.3.4z" />
      </svg>
    ),
    desc: (
      <svg
        stroke="currentColor"
        fill="currentColor"
        strokeWidth="0"
        viewBox="0 0 448 512"
        height="1em"
        width="1em"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: '0.3rem', marginTop: '-4px' }}
      >
        <path d="M413.1 222.5l22.2 22.2c9.4 9.4 9.4 24.6 0 33.9L241 473c-9.4 9.4-24.6 9.4-33.9 0L12.7 278.6c-9.4-9.4-9.4-24.6 0-33.9l22.2-22.2c9.5-9.5 25-9.3 34.3.4L184 343.4V56c0-13.3 10.7-24 24-24h32c13.3 0 24 10.7 24 24v287.4l114.8-120.5c9.3-9.8 24.8-10 34.3-.4z" />
      </svg>
    ),
  };

  const columns = [
    {
      key: 'code',
      label: 'Κωδικός Αίτησης',
      sortable: true,
    },
    {
      key: 'orderedDate',
      label: 'Ημερομηνία',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Κατάσταση Αίτησης',
      sortable: false,
    },
    {
      key: 'contactPerson',
      label: 'Υπεύθυνος Αίτησης',
      sortable: false,
    },
    // {
    //   key: 'notes',
    //   label: 'Σημειώσεις',
    //   sortable: false,
    // },
    {
      key: 'collectionLines',
      label: 'Συλλογή',
      sortable: false,
    },
    {
      key: 'actions',
      label: 'Ενέργειες',
      sortable: false,
    },
  ];

  const [getOrders, { data: rawData, loading, error, refetch }] =
    useLazyQuery(gqlGetCollectionOrders);
  const [sortColumn, setSortColumn] = useState('orderedDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [limit, setLimit] = useState(20); // default value
  const [page, setPage] = useState(1); // default page
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [getCollPointContainers] = useLazyQuery(gqlGetCollPointContainers);
  const [initialContainers, setInitialContainers] = useState([]);
  const client = useApolloClient();
  const currentYear = new Date().getFullYear();
  const handlePdfClick = async () => {
    try {
      const { data } = await client.query({
        query: gqlGetImaPdf,
        variables: { collectionPointId: id, year: currentYear - 1 },
        fetchPolicy: 'no-cache',
      });

      const base64 = data?.getImaPdf;
      if (!base64) {
        throw new Error('Δεν υπάρχουν δεδομένα για δημιουργία HMA');
      }

      const blob = new Blob([Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))], {
        type: 'application/pdf',
      });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err: any) {
      const msg =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.result?.errors?.[0]?.message ||
        err?.message ||
        'Παρουσιάστηκε σφάλμα: Δεν ήταν δυνατή η δημιουργία HMA';
      toastWarning(msg);
    }
  };

  function apiBase() {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3020';
    const url = process.env.REACT_APP_WEBAPI.replace('/graphql', ``);
    return url;
  }

  const handleCollectionCertificateClick = async (collectionId) => {
    const url = `${apiBase()}/pdf/waste-reception-certificate/${collectionId}`;
    window.open(url, '_blank').focus();
  };

  const handleConsignmentNoteClick = (orderId) => {
    if (!orderId) return;

    const url = `${apiBase()}/pdf/consignment-note/${orderId}`;
    window.open(url, '_blank').focus();
  };

  const preloadCollectorAndContainers = async () => {
    if (!id) return;

    const { data: containersData } = await getCollPointContainers({
      variables: { collectionPointId: id },
    });

    const containers = containersData?.getContainersOfCollectionPoint?.data || [];

    const mapped = containers
      .filter((c) => c.containerTypeId != null)
      .map((c) => ({
        containertypeId: Number(c.containerTypeId),
        containersQty: Number(c.containersQty),
        containertype: {
          id: Number(c.containerTypeId),
          name: c.containerTypeName ?? '',
        },
      }));
    setInitialContainers(mapped);
  };

  const handleOpenNewOrderModal = async () => {
    await preloadCollectorAndContainers();
    setShowNewOrderModal(true);
  };

  const orders = rawData?.getCollectionOrders?.data ?? [];
  const totalCount = rawData?.getCollectionOrders?.recordCount ?? 0;
  const handleOrderCreated = (id: number) => {
    refetch().then(({ data }) => {
      const order = data?.getCollectionOrders?.data?.find((o) => o.id === id);
      if (order) {
        setSelectedOrder(order);
      } else {
        toastWarning(`Η νέα αίτηση (#${id}) δεν βρέθηκε μετά τη δημιουργία`);
      }
    });
  };

  const handleSort = (column) => {
    setSortDirection((prev) => (sortColumn === column && prev === 'desc' ? 'asc' : 'desc'));
    setSortColumn(column);
  };

  const fetchOrders = useCallback(() => {
    getOrders({
      variables: {
        where: JSON.stringify({ collectionPointId: id, isActive: true }),
        offset: (page - 1) * limit,
        limit: limit,
        order: `${sortColumn ?? 'orderedDate'} ${sortDirection}`,
      },
    });
  }, [getOrders, id, sortColumn, sortDirection, limit, page]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (sortColumn) fetchOrders();
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [sortColumn, sortDirection, limit]);

  const RenderContainers = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Σύμφωνα με τα στοιχεία μας δεν έχετε εκκρεμείς αιτήσεις.
          </p>
          <OrdersActionsToolbar
            showCta
            onNewOrder={handleOpenNewOrderModal}
            onPdfClick={handlePdfClick}
          />
        </div>
      );
    }

    const hasPendingOrder = data.some((o) => o.isActive && o.statusId !== 3);

    const formatDate = (isoString: string) => new Date(isoString).toLocaleDateString('el-GR');

    return (
      <div>
        {hasPendingOrder ? (
          <>
            <div className="home-callout home-callout--warning">
              <p className="mb-0">
                Υπάρχει μία αίτηση συλλογής σε εκκρεμότητα. Η δυνατότητα καταχώρησης νέας αίτησης θα
                είναι διαθέσιμη μετά την ολοκλήρωσή της.
              </p>
            </div>
            <OrdersActionsToolbar
              showCta={false}
              onNewOrder={handleOpenNewOrderModal}
              onPdfClick={handlePdfClick}
            />
          </>
        ) : (
          <OrdersActionsToolbar
            showCta
            onNewOrder={handleOpenNewOrderModal}
            onPdfClick={handlePdfClick}
          />
        )}

        <div className="home-table-wrap">
          <table className="home-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={[
                      `home-table-col--${col.key}`,
                      col.sortable ? 'sortable' : '',
                      col.sortable && sortColumn === col.key ? 'sorted' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    {col.key === 'contactPerson' ? (
                      <>
                        <span className="d-inline d-sm-none">
                          Υπεύθυνος
                          <br />
                          Αίτησης
                        </span>
                        <span className="d-none d-sm-inline">Υπεύθυνος Αίτησης</span>
                      </>
                    ) : (
                      col.label
                    )}
                    {col.sortable && sortColumn === col.key && SortIcons[sortDirection]}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {data.map((order: any) => (
                <tr key={order.id}>
                  <td className="home-table-col--code" style={{ textAlign: 'center' }}>
                    {order.code}
                  </td>
                  <td className="home-table-col--orderedDate" style={{ textAlign: 'center' }}>
                    {formatDate(order.orderedDate)}
                  </td>
                  <td className="home-table-col--status" style={{ textAlign: 'center' }}>
                    <Badge
                      pill
                      className="badge-status home-status-badge text-white align-middle"
                      style={{
                        backgroundColor:
                          order.collectionOrderState.value === 1 ||
                          order.collectionOrderState.value === 2
                            ? '#fbb534'
                            : '#8DCE2A',
                      }}
                    >
                      {order.collectionOrderState.value === 1 ||
                      order.collectionOrderState.value === 2
                        ? 'Σε εκκρεμότητα'
                        : 'Ολοκληρωμένη'}
                    </Badge>
                  </td>
                  <td className="home-table-col--contactPerson" style={{ textAlign: 'center' }}>
                    {order.contactPerson ?? ''}
                  </td>
                  <td className="home-table-col--collectionLines" style={{ textAlign: 'left' }}>
                    {order.collection?.collectionLines?.length > 0 ? (
                      <div className="home-collection-lines">
                        {order.collection.collectionLines.map((line, idx) => {
                          const w = line.netWeight;
                          const weightStr =
                            typeof w === 'number'
                              ? w.toLocaleString('el-GR', { maximumFractionDigits: 2 })
                              : String(w ?? '');
                          return (
                            <div key={idx} className="home-collection-line">
                              <span className="home-collection-line__material">
                                {line.material.name}
                              </span>
                              <span className="home-collection-line__weight">{weightStr} kg</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ color: '#9a9a9a', fontStyle: 'italic' }}>—</span>
                    )}
                  </td>
                  <td className="home-table-col--actions">
                    {order.collection && (
                      <div className="home-row-actions">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="btn--doc"
                          onClick={() => handleCollectionCertificateClick(order.collection.id)}
                        >
                          <FaFilePdf className="mr-1" /> Βεβαίωση Παραλαβής
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="btn--doc"
                          onClick={() => handleConsignmentNoteClick(order.collection?.id)}
                        >
                          <FaFilePdf className="mr-1" /> Δελτίο Αποστολής
                        </Button>

                        {order.collection.idocs && order.collection.idocs.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline-warning"
                            onClick={() => {
                              const idocId = order.collection.idocs[0].id;
                              const url = `${apiBase()}/idoc/${idocId}/gr`;
                              window.open(url, '_blank').focus();
                            }}
                          >
                            Έντυπο Αναγνώρισης
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  }

  return (
    <Card className="home-section-card shadow-sm mt-4">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0 home-section-title">
            Αιτήσεις Συλλογής
          </Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body>
        <div>
          {loading ? (
            <p className="text-muted mb-0">Φόρτωση...</p>
          ) : totalCount === 0 ? (
            <>
              <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                Δεν υπάρχουν αιτήσεις.
              </p>
              <OrdersActionsToolbar
                showCta
                onNewOrder={handleOpenNewOrderModal}
                onPdfClick={handlePdfClick}
              />
            </>
          ) : (
            <RenderContainers data={orders} />
          )}
        </div>

        {totalCount > 0 && (
          <>
            <div className="home-section-footer">
              <small>(*) Παρουσιάζονται οι τελευταίες (έως {limit}) αιτήσεις ανά σελίδα.</small>
              <div>
                <label style={{ marginBlock: 'auto' }}>
                  Πλήθος αιτήσεων: <strong>{totalCount}</strong>
                </label>
                &nbsp;
                <select
                  className="home-page-size-select"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                >
                  <option value={5}>5</option>
                  <option value={7}>7</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            {orders.length > 0 && totalCount > limit && (
              <div className="home-pagination">
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="mr-2"
                >
                  Προηγούμενη
                </Button>
                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                  Σελίδα {page} από {Math.ceil(totalCount / limit)}
                </span>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setPage((p) => (p < Math.ceil(totalCount / limit) ? p + 1 : p))}
                  disabled={page >= Math.ceil(totalCount / limit)}
                  className="ml-2"
                >
                  Επόμενη
                </Button>
              </div>
            )}
          </>
        )}

        <NewCollectionOrderModal
          show={showNewOrderModal}
          onClose={() => {
            setSelectedOrder(null);
            setShowNewOrderModal(false);
          }}
          collectionPointId={id}
          onOrderCreated={handleOrderCreated}
          selectedOrder={selectedOrder}
          initialContainers={initialContainers}
        />
      </Card.Body>
    </Card>
  );
};
